import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { Button } from '../components';
import api from '../services/api';
import { getGespreksverslagen } from '../services/employerTrajectenApi';
import { B2bUpdates, Verlengingsverzoeken } from '../components/gespreksverslag/EmployerVerslagEngineSecties';

/**
 * Trajecten → Gespreksverslagen. Alle vrijgegeven verslagen van alle
 * professionals; de medewerker verschijnt uitsluitend als registratienummer.
 */
const TYPE_LABEL: Record<string, string> = { intake: 'Intake', session: 'Sessie', evaluation: 'Evaluatie', follow_up: 'Vervolg' };

const EmployerTrajectenGespreksverslagenPage: React.FC = () => {
  useCockpitTitle('Gespreksverslagen', 'Overzicht van alle professionals — op registratienummer, zonder namen');
  const [professionalId, setProfessionalId] = useState('');
  const [registratienummer, setRegistratienummer] = useState('');
  const [inclGesloten, setInclGesloten] = useState(false);

  const { data: alle = [] } = useQuery({ queryKey: ['trajecten-gespreksverslagen', '', '', true], queryFn: () => getGespreksverslagen({ include_closed: true }) });
  const { data: verslagen = [], isLoading } = useQuery({
    queryKey: ['trajecten-gespreksverslagen', professionalId, registratienummer, inclGesloten],
    queryFn: () => getGespreksverslagen({ professional_id: professionalId || undefined, registratienummer: registratienummer || undefined, include_closed: inclGesloten }),
  });
  const professionals = Array.from(new Map(alle.map((v) => [v.professional_id, v.professional_naam])).entries()).sort((a, b) => a[1].localeCompare(b[1]));
  const nummers = Array.from(new Set(alle.map((v) => v.registratienummer))).sort();

  return (
    <div className="px-4 sm:px-6 py-6 space-y-4">
      <Verlengingsverzoeken />
      <B2bUpdates />
      <div className="flex flex-wrap items-center gap-3">
        <select value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[200px]">
          <option value="">Alle professionals</option>
          {professionals.map(([id, naam]) => <option key={id} value={id}>{naam}</option>)}
        </select>
        <select value={registratienummer} onChange={(e) => setRegistratienummer(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]">
          <option value="">Alle registratienummers</option>
          {nummers.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={inclGesloten} onChange={(e) => setInclGesloten(e.target.checked)} className="rounded border-gray-300" />
          Ook afgesloten dossiers
        </label>
      </div>

      {isLoading ? (
        <p className="text-gray-600 py-12 text-center">Laden…</p>
      ) : verslagen.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-700 mb-1">Geen gespreksverslagen</p>
          <p className="text-sm text-gray-500">Verslagen verschijnen hier zodra de professional ze als rapportage vrijgeeft én de medewerker toestemming heeft gegeven.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Datum', 'Registratienummer', 'Professional', 'Discipline', 'Soort gesprek'].map((k) => (
                  <th key={k} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{k}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {verslagen.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50 text-sm text-gray-900">
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(v.datum).toLocaleDateString('nl-NL')}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono">{v.registratienummer}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{v.professional_naam || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{v.discipline ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{v.session_number || TYPE_LABEL[v.type ?? ''] || 'Overig'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1 ml-auto"
                      onClick={() => {
                        const base = (api.defaults.baseURL || '/api').replace(/\/$/, '');
                        window.open(`${base}/employer/dossiers/${v.dossier_id}/verslagen/${v.id}/pdf?t=${Date.now()}`, '_blank');
                      }}
                    >
                      <Download className="w-4 h-4" />PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployerTrajectenGespreksverslagenPage;
